import { queryApprovalRequests } from '@wix/ambassador-agentmaker-approvals-v1-approval-request/http';
import { Status as ApprovalRequestStatus } from '@wix/ambassador-agentmaker-approvals-v1-approval-request/types';
import { queryInvocations } from '@wix/ambassador-agentmaker-invocations-v1-invocation/http';
import { queryActivities } from '@wix/ambassador-agentmaker-v1-activity/http';
import { ToolCallStatus, Type as ActivityType } from '@wix/ambassador-agentmaker-v1-activity/types';
import { RunTaskEntryApprovalStatus, RunTaskEntryStatus, RunTaskEntryType } from '../constants';
import { shouldShowApprovalEdit } from './shouldShowApprovalEdit';
export async function fetchEntries(_ref) {
  let {
    httpClient,
    invocationId,
    automation,
    shouldAlwaysFetchApprovals,
    t
  } = _ref;
  const entries = [];
  const shouldWaitForAutomation = !invocationId && !!automation;
  if (shouldWaitForAutomation) {
    const automationId = automation.id;
    const invocation = await fetchAutomationInvocation({
      automationId,
      httpClient
    });
    if (invocation) {
      invocationId = invocation.id;
    }
  }
  if (invocationId) {
    const fetchEntriesDataResponse = await fetchEntriesData({
      httpClient,
      invocationId,
      shouldAlwaysFetchApprovals
    });
    const {
      activities,
      approvalRequests
    } = fetchEntriesDataResponse;
    entries.push(...sortEntriesByDate(mapActivitiesToEntries(activities, approvalRequests, t)));
  }
  const hasCompletedInvocation = entries.some(entry => entry.type === RunTaskEntryType.End);
  const hasPendingEntries = entries.some(entry => [RunTaskEntryStatus.Pending, RunTaskEntryStatus.Loading].includes(entry.status));
  return {
    entries,
    invocationId,
    isPending: !hasCompletedInvocation || hasPendingEntries,
    shouldAlwaysFetchApprovals
  };
}
const mapActivitiesToEntries = (activities, approvalRequests, t) => {
  const invocationFinishedActivity = activities.find(activity => activity.type === ActivityType.INVOCATION_FINISHED);
  const invocationCanceledActivity = activities.find(activity => activity.type === ActivityType.INVOCATION_CANCELED);
  const invocationFailedActivity = activities.find(activity => activity.type === ActivityType.INVOCATION_FAILED);
  const timedOutActivity = activities.find(activity => activity.type === ActivityType.TIMED_OUT);
  const hasCompletedInvocation = !!invocationFinishedActivity || !!invocationCanceledActivity || !!invocationFailedActivity || !!timedOutActivity;
  const entryDetailsByToolCallId = mapActivitiesToEntryDetailsByToolCall(activities, hasCompletedInvocation);
  const toolCallEntries = activities.reduce((entries, activity) => {
    const date = activity.activityDate;
    const newEntries = [...entries];
    if (activity.type === ActivityType.TOOL_CALLS_REQUESTED) {
      var _activity$toolCallsRe;
      const newToolCallEntries = (_activity$toolCallsRe = activity.toolCallsRequested) == null || (_activity$toolCallsRe = _activity$toolCallsRe.toolCallRequests) == null ? void 0 : _activity$toolCallsRe.map(toolCall => {
        const requiresApproval = toolCall.requiresApproval ?? false;
        const toolCallId = toolCall.id;
        const toolName = toolCall.name;
        const entryDetails = entryDetailsByToolCallId[toolCallId];
        const {
          inProgress,
          requestPayload,
          resultPayload,
          title,
          message
        } = entryDetails;
        const approvalRequest = requiresApproval ? approvalRequests.find(request => request.toolCallId === toolCallId) : undefined;
        const approvalId = (approvalRequest == null ? void 0 : approvalRequest.id) ?? undefined;
        const showApprovalEdit = shouldShowApprovalEdit(approvalRequest);
        const status = requiresApproval ? getEntryStatusFromApproval(entryDetails.status, approvalRequest) : entryDetails.status;
        const approvalStatus = requiresApproval ? getEntryApprovalStatus(approvalRequest) : undefined;
        return {
          type: RunTaskEntryType.ToolCall,
          date,
          toolName,
          toolCallId,
          requiresApproval,
          approvalId,
          showApprovalEdit,
          approvalStatus,
          status,
          inProgress,
          requestPayload,
          resultPayload,
          title,
          message
        };
      });
      newEntries.push(...(newToolCallEntries ?? []));
    }
    return newEntries;
  }, []);
  const allEntries = [...toolCallEntries];
  if (invocationFinishedActivity) {
    var _invocationFinishedAc, _invocationFinishedAc2;
    // TODO: Temp compatibility until specs.agentmaker.DontStopOnReject experiment is merged
    const hasFinishedWithFailure = ((_invocationFinishedAc = invocationFinishedActivity.invocationFinished) == null ? void 0 : _invocationFinishedAc.content) === 'Tool was rejected.';
    allEntries.push({
      type: RunTaskEntryType.End,
      status: hasFinishedWithFailure ? RunTaskEntryStatus.Failed : RunTaskEntryStatus.Completed,
      date: invocationFinishedActivity.activityDate,
      title: hasFinishedWithFailure ? t('task.failed.title') : t('task.succeeded.title'),
      message: ((_invocationFinishedAc2 = invocationFinishedActivity.invocationFinished) == null ? void 0 : _invocationFinishedAc2.content) ?? ''
    });
  }
  if (invocationCanceledActivity) {
    allEntries.push({
      type: RunTaskEntryType.End,
      status: RunTaskEntryStatus.Failed,
      date: invocationCanceledActivity.activityDate,
      title: t('task.canceled.title'),
      message: t('task.canceled.message')
    });
  }
  if (invocationFailedActivity) {
    allEntries.push({
      type: RunTaskEntryType.End,
      status: RunTaskEntryStatus.Failed,
      date: invocationFailedActivity.activityDate,
      title: t('task.failed.title'),
      message: getFailedEntryMessage(t, invocationFailedActivity.invocationFailed),
      resultPayload: invocationFailedActivity.invocationFailed
    });
  }
  if (timedOutActivity) {
    allEntries.push({
      type: RunTaskEntryType.End,
      status: RunTaskEntryStatus.Failed,
      date: timedOutActivity.activityDate,
      title: t('task.timed-out.title'),
      message: t('task.timed-out.message')
    });
  }
  return allEntries;
};
const sortEntriesByDate = entries => {
  return entries.slice().sort((a, b) => {
    const dateA = a.date.getTime();
    const dateB = b.date.getTime();
    if (dateA < dateB) {
      return -1;
    }
    if (dateA > dateB) {
      return 1;
    }
    return 0;
  });
};
const mapActivitiesToEntryDetailsByToolCall = (activities, hasCompletedInvocation) => {
  return activities.reduce((result, activity) => {
    const detailsByToolCallId = {};
    if (activity.type === ActivityType.TOOL_CALLS_REQUESTED) {
      for (const toolCall of ((_activity$toolCallsRe2 = activity.toolCallsRequested) == null ? void 0 : _activity$toolCallsRe2.toolCallRequests) ?? []) {
        var _activity$toolCallsRe2;
        const toolCallId = toolCall.id;
        const title = toolCall.title;
        if (!result[toolCallId]) {
          // Tool call entry will be marked as failed when invocation has completed and `TOOLS_CALLED` is missing
          const status = hasCompletedInvocation ? RunTaskEntryStatus.Failed : RunTaskEntryStatus.Loading;
          detailsByToolCallId[toolCallId] = {
            status,
            inProgress: true,
            title,
            message: toolCall.description ?? undefined,
            requestPayload: toolCall
          };
        } else {
          result[toolCallId].title = title;
          result[toolCallId].requestPayload = toolCall;
        }
      }
    } else if (activity.type === ActivityType.TOOLS_CALLED) {
      for (const toolCall of ((_activity$toolsCalled = activity.toolsCalled) == null ? void 0 : _activity$toolsCalled.toolCalls) ?? []) {
        var _activity$toolsCalled;
        const toolCallId = toolCall.id;
        const message = toolCall.description ?? undefined;
        const commonToolCallDetails = {
          ...result[toolCallId],
          inProgress: false,
          message,
          resultPayload: toolCall
        };
        if (toolCall.status === ToolCallStatus.ERROR || toolCall.status === ToolCallStatus.REJECTED) {
          detailsByToolCallId[toolCallId] = {
            ...commonToolCallDetails,
            status: RunTaskEntryStatus.Failed
          };
        } else if (toolCall.status === ToolCallStatus.SUCCESS) {
          detailsByToolCallId[toolCallId] = {
            ...commonToolCallDetails,
            status: RunTaskEntryStatus.Succeeded
          };
        }
      }
    }
    return {
      ...result,
      ...detailsByToolCallId
    };
  }, {});
};
const fetchAutomationInvocation = async _ref2 => {
  var _queryInvocationsResp;
  let {
    automationId,
    httpClient
  } = _ref2;
  const queryInvocationsResponse = await httpClient.request(queryInvocations({
    query: {
      filter: {
        'automationTriggerMetadata.automationId': automationId
      },
      cursorPaging: {
        limit: 1
      }
    }
  }));
  return (_queryInvocationsResp = queryInvocationsResponse.data.invocations) == null ? void 0 : _queryInvocationsResp[0];
};
const getEntryStatusFromApproval = (statusFromActivity, approvalRequest) => {
  if (!approvalRequest) {
    return statusFromActivity;
  }
  if (approvalRequest.status === ApprovalRequestStatus.PENDING) {
    return RunTaskEntryStatus.Pending;
  }
  if (approvalRequest.status === ApprovalRequestStatus.REJECTED) {
    return RunTaskEntryStatus.Failed;
  }

  // NOTE: We don't handle ACCEPTED status here since we want to show tool call status from activity
  // (tool call may fail even if the approval was accepted)

  return statusFromActivity;
};
const getEntryApprovalStatus = approvalRequest => {
  if ((approvalRequest == null ? void 0 : approvalRequest.status) === ApprovalRequestStatus.ACCEPTED) {
    return RunTaskEntryApprovalStatus.Approved;
  }
  if ((approvalRequest == null ? void 0 : approvalRequest.status) === ApprovalRequestStatus.REJECTED) {
    return RunTaskEntryApprovalStatus.Rejected;
  }
  return RunTaskEntryApprovalStatus.Pending;
};
const fetchEntriesData = async _ref3 => {
  let {
    httpClient,
    invocationId,
    shouldAlwaysFetchApprovals
  } = _ref3;
  const activitiesRequest = queryActivities({
    query: {
      filter: {
        invocationId
      }
    }
  });
  const approvalsRequest = queryApprovalRequests({
    query: {
      filter: {
        invocationId
      }
    }
  });
  let activities = [];
  let approvalRequests = [];
  if (shouldAlwaysFetchApprovals) {
    const [activitiesResponse, approvalsResponse] = await Promise.all([httpClient.request(activitiesRequest), httpClient.request(approvalsRequest)]);
    activities = activitiesResponse.data.activities ?? [];
    approvalRequests = approvalsResponse.data.approvalRequests ?? [];
  } else {
    const activitiesResponse = await httpClient.request(activitiesRequest);
    activities = activitiesResponse.data.activities ?? [];
    const hasActivitiesWithApprovalRequests = activities.some(activity => {
      var _activity$toolCallsRe3;
      if (activity.type !== ActivityType.TOOL_CALLS_REQUESTED) {
        return false;
      }
      const toolCallRequests = ((_activity$toolCallsRe3 = activity.toolCallsRequested) == null ? void 0 : _activity$toolCallsRe3.toolCallRequests) ?? [];
      return toolCallRequests.some(toolCall => !!toolCall.requiresApproval);
    });
    if (hasActivitiesWithApprovalRequests) {
      const approvalsResponse = await httpClient.request(approvalsRequest);
      approvalRequests = approvalsResponse.data.approvalRequests ?? [];
      shouldAlwaysFetchApprovals = true;
    }
  }
  return {
    activities,
    approvalRequests,
    shouldAlwaysFetchApprovals
  };
};
const getFailedEntryMessage = (t, invocationFailed) => {
  if ((invocationFailed == null ? void 0 : invocationFailed.errorCode) === 'EXECUTION_LIMITS_REACHED') {
    return t('task.failed.execution-limit.message');
  }
  if ((invocationFailed == null ? void 0 : invocationFailed.errorCode) === 'PERMISSION_DENIED') {
    return t('task.failed.permission.message');
  }
  return t('task.failed.generic.message');
};
//# sourceMappingURL=fetchEntries.js.map